import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import {
  AlertTriangleIcon,
  ArrowDownCappedIcon,
  ChartIncreaseIcon,
  FarmingIcon,
  MultiNftIcon,
  TickIcon,
} from "@/domains/icons"
import { CoinsIcon } from "@/domains/icons/huge-icons/monotone/coins"
import { claimClFarmRewardInteraction } from "@/domains/interactions"
import { cn, dollars, dollarsPrettified } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"
import { SCALE } from "@/domains/tokens/static-tokens"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

export const ClFarmsSummaryCard = observer(() => {
  const account = useAccount()
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()
  const [isClaiming, setIsClaiming] = useState(false)

  // Get all CL farms with positions or rewards (same method as CL farming rewards view)
  const clFarmsWithRewards = scaleApp.ClFarms.Repository.getAllWithGaugeRewards()

  // Get all CL farms to check for positions (staked or unstaked)
  const allClFarms = scaleApp.ClFarms.Repository.getAll()

  // Filter for farms with ANY position (staked OR unstaked) - same logic as clfarms-processor
  // A position exists if user has unstaked balance OR staked balance
  const clFarmsWithPositions = allClFarms.filter((farm) => {
    if (!farm.gaugeAddress) {
      return false
    }
    const hasStakedPosition =
      farm.onChainUserGaugeBalance > 0n || farm.onChainUserGaugeReserve0 > 0n || farm.onChainUserGaugeReserve1 > 0n
    const hasUnstakedPosition = farm.onChainUserBalance > 0n
    return hasStakedPosition || hasUnstakedPosition
  })

  // Calculate total deposited (staked + unstaked) for all positions
  const totalDeposited = clFarmsWithPositions.reduce((sum, farm) => {
    const stakedValue = farm.gaugeUserBalanceValueUSD
    const unstakedValue = farm.userBalanceValueUSD
    return sum + stakedValue + unstakedValue
  }, 0)

  const totalEarned = clFarmsWithRewards.reduce((sum, farm) => {
    const rewardAmount = SCALE.formatUnits(farm.onChainUserGaugeGovernanceEarned)
    return sum + Number(rewardAmount)
  }, 0)
  const rewardsUsd = SCALE.calculateValueUSD(totalEarned)

  // Top APR - from farms with any position (staked or unstaked)
  const maxApr = clFarmsWithPositions.length > 0 ? Math.max(...clFarmsWithPositions.map((farm) => farm.apr)) : 0

  // Position count - farms with any position (staked or unstaked)
  const positionCount = clFarmsWithPositions.length

  // Status: Check if user has any unstaked positions (in wallet but not staked)
  const hasUnstakedPositions = clFarmsWithPositions.some((farm) => farm.onChainUserBalance > 0n)
  const isAllStaked = !hasUnstakedPositions && positionCount > 0

  // Check if there are rewards to claim
  const hasRewards = totalEarned > 0

  const handleClaimAll = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click from scrolling
    if (!hasRewards || isClaiming) return

    setIsClaiming(true)
    try {
      if (!account.address) {
        throw new Error("no address found")
      }

      // Claim all CL farm rewards
      for (const farm of clFarmsWithRewards) {
        if (!farm.farmAddress || !farm.voterAddress || farm.onChainUserGaugeGovernanceEarned === 0n) {
          continue
        }

        await claimClFarmRewardInteraction({
          account: account.address,
          chainId: chainConfig.chainId,
          config,
          voterAddress: farm.voterAddress.toViemAddress() as `0x${string}`,
          farmAddress: farm.farmAddress.toViemAddress() as `0x${string}`,
          earnedAddresses: [(SCALE.address as any).toViemAddress?.() ?? SCALE.address] as Array<`0x${string}`>,
          pairDisplayName: farm.displayName ?? "unknown",
          emitter,
        })
      }
    } catch (error) {
      console.error("ClFarmsSummaryCard→handleClaimAll error", error)
    } finally {
      setIsClaiming(false)
    }
  }

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click from scrolling
    const element = document.getElementById("cl-farm-farming-rewards")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const handleCardClick = () => {
    const element = document.getElementById("cl-farm-farming-rewards")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative flex w-full flex-col overflow-hidden rounded-3xl border border-p400-light bg-p300 shadow-2xl transition-all",
        "hover:border-p400-light/80",
      )}
    >
      {/* Decorative Glow */}
      {/* <div className="from-indigo-500/5 pointer-events-none absolute inset-0 bg-gradient-to-br via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" /> */}

      {/* Header */}
      <div className="relative z-10 p-6 pb-6">
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-t300/20 bg-p400/10 text-t300">
              <FarmingIcon className="h-5 w-5 text-t300" />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight text-t100">CL Farms</h3>
              <p className="text-xs font-medium text-t300">Concentrated Liquidity</p>
            </div>
          </div>

          {/* Status Pill */}
          {positionCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-green-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500"></span>
              </span>
              Active
            </div>
          )}
        </div>

        {/* Hero Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Total Deposited</p>
            <div className="text-2xl font-bold tracking-tight text-t100">{dollarsPrettified(totalDeposited, 2)}</div>
          </div>
          <div className="text-right">
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Unclaimed Rewards</p>
            <div className="text-2xl font-bold tracking-tight text-t300">{dollarsPrettified(rewardsUsd, 2)}</div>
            <div className="mt-0.5 flex items-center justify-end gap-1.5 text-[10px] font-medium text-t300">
              <DisplayTokens tokens={[SCALE]} tokenSize={14} />
              <span>{totalEarned.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 border-y border-t300/[0.12] bg-t300/[0.02]">
        {/* Positions Count */}
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Positions</span>
          <span className="flex items-center gap-1 text-sm font-bold text-t100">
            <FarmingIcon className="h-3.5 w-3.5 text-t200" /> {positionCount}
          </span>
        </div>

        {/* Top APR */}
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Top APR</span>
          <span className="flex items-center gap-1 text-sm font-bold text-green-400">
            <ChartIncreaseIcon className="h-3.5 w-3.5" /> {maxApr.toFixed(1)}%
          </span>
        </div>

        {/* Status Signal */}
        <div className="flex flex-col items-center justify-center p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Status</span>
          {isAllStaked ? (
            <span className="flex items-center gap-1 text-sm font-bold text-green-400">
              <TickIcon className="h-3.5 w-3.5" /> All Staked
            </span>
          ) : hasUnstakedPositions ? (
            <span className="flex items-center gap-1 text-sm text-red-400">
              <AlertTriangleIcon className="h-3.5 w-3.5" /> Unstaked
            </span>
          ) : (
            <span className="text-sm font-bold text-t200">—</span>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="z-10 mt-auto flex gap-3 bg-p300 p-4">
        {/* Secondary Action: View Details */}
        <Button
          variant="percentage"
          size="sm"
          onClick={handleViewDetails}
          className="text-xs font-bold uppercase tracking-wide"
        >
          Details <ArrowDownCappedIcon className="h-3.5 w-3.5" />
        </Button>

        {/* Primary Action: Claim All */}
        <Button
          variant={hasRewards ? "primary" : "percentage"}
          size="sm"
          onClick={handleClaimAll}
          disabled={!hasRewards || isClaiming}
          className="flex-1 font-bold"
        >
          {isClaiming ? (
            <>
              <span className="animate-spin">⟳</span> Claiming...
            </>
          ) : (
            <>
              <CoinsIcon className="h-4 w-4" /> Claim All
            </>
          )}
        </Button>
      </div>
    </div>
  )
})
